import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getManageClubContact,
  getManageClubCss,
  getManageClubSignIn,
  updateManageClubContact,
  updateManageClubCss,
  updateManageClubSignIn,
  type ClubContact,
} from "../lib/api";
import { useScopedStyle } from "../lib/clubTheme";
import { ManageClubNav, useManagedClub } from "../components/ManageClubNav";
import ClubContactFields, { contactDraft } from "../components/ClubContactFields";
import Toast from "../components/Toast";

// Theme variables from styles.css :root that a club can override.
const THEME_VARIABLES: [string, string][] = [
  ["--accent", "Buttons, links, nav underline (#d92421)"],
  ["--accent-hover", "Hover color (#f74a48)"],
  ["--accent-active", "Pressed color (#810f0d)"],
  ["--bg", "Page background"],
  ["--panel", "Card background"],
  ["--panel-2", "Light gray panels"],
  ["--border", "Borders"],
  ["--text", "Body text"],
  ["--muted", "Secondary text"],
  ["--footer-bg", "Footer background"],
  ["--footer-text", "Footer text"],
  ["--heading-font", "Headings and wordmark font"],
];
const CLASS_HOOKS = ["nav", ".brand-text", "main", "h1, h2, h3", ".card", "button", ".pill", ".events-grid", ".site-footer"];

const EXAMPLE = `/* Variables apply to the whole club site */
--accent: #0055ff;
--accent-hover: #3d7dff;
--accent-active: #003399;

/* Rules only match inside your club's site */
.card { border-radius: 12px; }
h1 { letter-spacing: 0.05em; }`;

/** Cheap client-side check so a half-typed rule can't break this page's
 *  preview. The server (lib/clubCss.ts) is the real gate on save. */
function previewBlocker(css: string): string | null {
  if (/url\s*\(|@import|image-set\s*\(|</i.test(css)) return "it uses url(), @import or \"<\", which won't be accepted";
  let depth = 0;
  for (const ch of css) {
    if (ch === "{") depth++;
    else if (ch === "}" && --depth < 0) break;
  }
  return depth === 0 ? null : "the braces { } don't balance yet";
}

/** /manage/:clubId/advanced -- the club's contact info (About page) and
 *  custom CSS ("with your look"). */
export default function ManageAdvancedPage() {
  const { clubId = "" } = useParams();
  const { club, error: clubError } = useManagedClub(clubId);
  const [css, setCss] = useState<string | null>(null);
  const [saved, setSaved] = useState("");
  const [meta, setMeta] = useState<{ at: string | null; by: string | null; maxBytes: number }>({ at: null, by: null, maxBytes: 20480 });
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getManageClubCss(clubId)
      .then((res) => {
        setCss(res.customCss);
        setSaved(res.customCss);
        setMeta({ at: res.customCssUpdatedAt, by: res.customCssUpdatedBy, maxBytes: res.maxBytes });
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load custom CSS."));
  }, [clubId]);

  const blocker = css ? previewBlocker(css) : null;
  useScopedStyle("club-css-preview", ".club-theme-preview", preview && css && !blocker ? css : "");

  async function save(next: string) {
    setError(null);
    setSaving(true);
    try {
      const res = await updateManageClubCss(clubId, next);
      setCss(res.customCss);
      setSaved(res.customCss);
      setMeta({ at: res.customCssUpdatedAt, by: res.customCssUpdatedBy, maxBytes: res.maxBytes });
      setInfo(res.customCss ? "Custom CSS saved. Your club's site picks it up within a minute." : "Reset to the default look.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    if (!window.confirm("Remove your custom CSS and go back to the default look?")) return;
    void save("");
  }

  if (clubError) return <p className="err">{clubError}</p>;
  if (!club || css === null) return error ? <p className="err">{error}</p> : <p>Loading…</p>;

  const bytes = new TextEncoder().encode(css).length;

  return (
    <div>
      <ManageClubNav club={club} />
      <h1>{club.name} advanced settings</h1>
      {error && <p className="err">{error}</p>}
      {info && <Toast message={info} onDismiss={() => setInfo(null)} />}

      <ContactInfoCard clubId={clubId} onSaved={setInfo} />
      <SignInCard clubId={clubId} onSaved={setInfo} />

      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          void save(css);
        }}
      >
        <h3 style={{ marginTop: 0 }}>Custom CSS</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          Give your club's site its own look. Your CSS applies on your club's domain, inside the page layout, but never on
          sign-in, checkout, account, Manage or Admin pages. Images and fonts from other sites (<code>url()</code>,{" "}
          <code>@import</code>) aren't allowed.
        </p>
        <label htmlFor="club-css">CSS</label>
        <textarea
          id="club-css"
          value={css}
          onChange={(e) => setCss(e.target.value)}
          placeholder={EXAMPLE}
          rows={16}
          spellCheck={false}
          style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 13, width: "100%" }}
        />
        <p className="muted" style={{ fontSize: 12 }}>
          {(bytes / 1024).toFixed(1)} KB of {meta.maxBytes / 1024} KB
          {meta.at && ` · last saved ${new Date(meta.at).toLocaleString()}${meta.by ? ` by ${meta.by}` : ""}`}
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="submit" disabled={saving || css === saved}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" className="secondary" onClick={() => setPreview((p) => !p)}>
            {preview ? "Hide preview" : "Preview"}
          </button>
          <button type="button" className="secondary" onClick={handleReset} disabled={saving || !saved}>
            Reset to default
          </button>
        </div>
      </form>

      {preview && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Preview</h3>
          {blocker && <p className="err">Preview paused: {blocker}.</p>}
          <div className="club-theme-preview" style={{ background: "var(--bg)", color: "var(--text)", padding: "1rem", border: "1px dashed var(--border)" }}>
            <h2 style={{ fontFamily: "var(--heading-font)" }}>Saturday club race</h2>
            <div className="card">
              <p>
                Doors open at 8am. <a href="#preview" onClick={(e) => e.preventDefault()}>Event details</a>
              </p>
              <span className="pill">Open</span>{" "}
              <span className="muted">2WD Buggy · 4WD SCT</span>
            </div>
            <button type="button">Register</button>{" "}
            <button type="button" className="secondary">Cancel</button>
          </div>
          <p className="muted" style={{ fontSize: 12 }}>
            Only this box is previewed. Save, then open your club's site to see the full result.
          </p>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>What you can style</h3>
        <p className="muted" style={{ fontSize: 13 }}>
          Your CSS is wrapped in <code>.club-theme {"{ … }"}</code>. Set variables as plain declarations at the top to
          change colors everywhere, then add rules for anything else.
        </p>
        <ul style={{ fontSize: 13 }}>
          {THEME_VARIABLES.map(([name, desc]) => (
            <li key={name}>
              <code>{name}</code>: {desc}
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 13 }}>
          Common selectors: {CLASS_HOOKS.map((c, i) => (
            <span key={c}>
              {i > 0 && ", "}
              <code>{c}</code>
            </span>
          ))}
        </p>
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>Example</summary>
          <pre style={{ fontSize: 12, whiteSpace: "pre-wrap" }}>{EXAMPLE}</pre>
          <button type="button" className="secondary" onClick={() => setCss(css ? `${css}\n\n${EXAMPLE}` : EXAMPLE)}>
            Insert example
          </button>
        </details>
      </div>
    </div>
  );
}

/** The club's public contact details, shown on its About page. The contact
 *  email is also where the About page's contact form delivers. */
function ContactInfoCard({ clubId, onSaved }: { clubId: string; onSaved: (message: string) => void }) {
  const [contact, setContact] = useState<ClubContact | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getManageClubContact(clubId)
      .then((res) => setContact(contactDraft(res.contact)))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load contact info."));
  }, [clubId]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!contact) return;
    setError(null);
    setSaving(true);
    try {
      const res = await updateManageClubContact(clubId, contact);
      setContact(contactDraft(res.contact));
      setDirty(false);
      onSaved("Contact info saved. Your club's About page picks it up within a minute.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="card" onSubmit={handleSave}>
      <h3 style={{ marginTop: 0 }}>Contact info</h3>
      <p className="muted" style={{ fontSize: 13 }}>
        Shown on your club's About page as plain text. Visitors' messages from its contact form are emailed to the
        contact email, with their address as Reply-To. Leave the email blank to turn the form off.
      </p>
      {error && <p className="err">{error}</p>}
      {contact === null ? (
        !error && <p>Loading…</p>
      ) : (
        <>
          <ClubContactFields
            idPrefix="manage-contact"
            value={contact}
            onChange={(next) => {
              setContact(next);
              setDirty(true);
            }}
          />
          <button type="submit" disabled={saving || !dirty}>{saving ? "Saving…" : "Save contact info"}</button>
        </>
      )}
    </form>
  );
}

/** Whether "Sign in with Facebook" shows on the club's own site. */
function SignInCard({ clubId, onSaved }: { clubId: string; onSaved: (message: string) => void }) {
  const [facebook, setFacebook] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getManageClubSignIn(clubId)
      .then((res) => setFacebook(res.facebook))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load sign-in options."));
  }, [clubId]);

  async function handleChange(next: boolean) {
    setError(null);
    setSaving(true);
    try {
      const res = await updateManageClubSignIn(clubId, next);
      setFacebook(res.facebook);
      onSaved(
        res.facebook
          ? "Facebook sign-in is on. It works on your club's site within a few minutes."
          : "Facebook sign-in is off for your club's site. Email sign-in still works.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Sign-in</h3>
      {error && <p className="err">{error}</p>}
      {facebook === null ? (
        !error && <p>Loading…</p>
      ) : (
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <input
            type="checkbox"
            checked={facebook}
            disabled={saving}
            onChange={(e) => void handleChange(e.target.checked)}
            style={{ width: "auto" }}
          />
          Allow "Sign in with Facebook" on your club's site
        </label>
      )}
      <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
        Members can always sign in with email. Facebook accounts are shared across RC Race HQ, so a racer uses the
        same account at every club.
      </p>
    </div>
  );
}
